(() => {
  if (!('IntersectionObserver' in window)) return;
  const tracked = new WeakSet();
  const promote = (image) => {
    image.loading = 'eager';
    image.decoding = 'async';
  };
  const nearby = new IntersectionObserver((entries, observer) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const track = entry.target;
      observer.unobserve(track);
      const images = [...track.querySelectorAll('img[loading="lazy"]')];
      const ahead = new IntersectionObserver((slides, slideObserver) => {
        for (const slide of slides) {
          if (!slide.isIntersecting) continue;
          promote(slide.target);
          slideObserver.unobserve(slide.target);
        }
      }, { root: track, rootMargin: '0px 100% 0px 100%' });
      for (const image of images) ahead.observe(image);
      track.addEventListener('focusin', (event) => {
        const card = event.target.closest('.grid__item, .bf-home__category');
        card?.querySelectorAll('img[loading="lazy"]').forEach(promote);
      });
    }
  }, { rootMargin: '400px 0px' });
  const initialize = (root) => {
    root.querySelectorAll('.bf-home__grid, .bf-featured .slider').forEach((track) => {
      if (tracked.has(track)) return;
      tracked.add(track);
      nearby.observe(track);
    });
  };
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  // Keep the home category rail moving gently while it is visible and idle.
  // Native scrolling remains available; interaction and reduced-motion settings pause autoplay.
  const categoryRails = new WeakSet();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initializeCategoryRails = (root) => {
    root.querySelectorAll('bf-category-carousel').forEach((carousel) => {
      if (categoryRails.has(carousel)) return;
      const track = carousel.querySelector('.bf-home__grid');
      if (!track) return;
      categoryRails.add(carousel);

      let visible = false;
      let hovered = false;
      let focused = false;
      let interactionTimer;
      let advanceTimer;
      let movingAutomatically = false;
      const cards = [...track.querySelectorAll('.bf-home__category')];
      const clearAdvance = () => window.clearTimeout(advanceTimer);
      const schedule = () => {
        clearAdvance();
        if (!visible || hovered || focused || document.hidden || reducedMotion.matches || cards.length < 3) return;
        advanceTimer = window.setTimeout(() => {
          const maxScroll = track.scrollWidth - track.clientWidth;
          if (maxScroll <= 1) return;
          const atEnd = track.scrollLeft >= maxScroll - 8;
          const trackLeft = track.getBoundingClientRect().left;
          const nextCard = cards.find((card) => card.getBoundingClientRect().left > trackLeft + 8);
          movingAutomatically = true;
          if (atEnd || !nextCard) {
            track.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            track.scrollBy({ left: nextCard.getBoundingClientRect().left - trackLeft, behavior: 'smooth' });
          }
          window.setTimeout(() => { movingAutomatically = false; schedule(); }, 850);
        }, 4500);
      };
      const pauseAfterInteraction = () => {
        clearAdvance();
        window.clearTimeout(interactionTimer);
        interactionTimer = window.setTimeout(schedule, 7000);
      };

      const visibilityObserver = new IntersectionObserver((entries) => {
        visible = entries[0]?.isIntersecting ?? false;
        schedule();
      }, { rootMargin: '80px 0px' });
      visibilityObserver.observe(carousel);

      carousel.addEventListener('pointerenter', () => { hovered = true; clearAdvance(); });
      carousel.addEventListener('pointerleave', () => { hovered = false; schedule(); });
      carousel.addEventListener('focusin', () => { focused = true; clearAdvance(); });
      carousel.addEventListener('focusout', (event) => {
        if (!carousel.contains(event.relatedTarget)) { focused = false; schedule(); }
      });
      track.addEventListener('pointerdown', pauseAfterInteraction, { passive: true });
      track.addEventListener('keydown', pauseAfterInteraction);
      track.addEventListener('wheel', (event) => { if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) pauseAfterInteraction(); }, { passive: true });
      track.addEventListener('scroll', () => { if (!movingAutomatically) pauseAfterInteraction(); }, { passive: true });
      document.addEventListener('visibilitychange', schedule);
      reducedMotion.addEventListener('change', schedule);
    });
  };
  initializeCategoryRails(document);
  document.addEventListener('shopify:section:load', (event) => initializeCategoryRails(event.target));
})();
